import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["alternate", "children", "className", "compact", "disabled", "external", "externalLinkText", "finish", "ghost", "icon", "iconLabel", "iconPos", "iconProps", "loading", "minified", "nonInteractive", "primary", "size", "statusMessage", "target"],
  _excluded2 = ["href"],
  _excluded3 = ["fwdRef"],
  _excluded4 = ["fwdRef", "onClick"];
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IconLinkExternal } from '@op/opds.opux-icons/navigation';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { color as colorTokens } from '@op/opds.tokens';
import { FormContext } from '../form/form';
import { ButtonRowContext } from '../button-row/button-row';
import Loading from '../loading/loading';
import StatusMessage from '../status-message/status-message';
import { default as actionMessages } from '../action/_messages';
import { default as loadingMessages } from '../loading/_messages';
import { useLocale } from '../common/locale';
const logErrorUnlessInProd = message => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(message);
  }
};
const getType = props => {
  const types = ['finish', 'primary', 'alternate', 'compact'];
  const trueTypes = types.filter(t => props[t]);
  const type = trueTypes[0];
  if (trueTypes.length > 1) {
    logErrorUnlessInProd("Button can't be " + trueTypes.join(' and ') + " at the same time, " + type + " takes precedence");
  }
  return type;
};
const checkIconOnlyProps = (iconOnly, type, props) => {
  const {
    children,
    ghost,
    iconLabel,
    minified
  } = props;
  if (iconOnly && !iconLabel) {
    logErrorUnlessInProd('Prop iconLabel is required for an icon-only Button to describe it for screen reader users.');
  }
  if (minified && children) {
    logErrorUnlessInProd('Minified mode is not allowed for button with children');
  }
  if (ghost && children) {
    logErrorUnlessInProd('Ghost mode is not allowed for button with children');
  }
  if (ghost && type && type !== 'compact') {
    logErrorUnlessInProd('Ghost mode is only allowed for default and compact Button');
  }
};

/**
 * _Buttons let users to perform important, clearly expressed actions._
 *
 * ***
 *
 * The component children should be text. Icon and loading indicator
 * are included via props. For buttons with only an icon `aria-label`
 * attribute need to be set with the `iconLabel` prop. The `loading`
 * prop sets the `aria-busy` attribute.
 *
 * Additional props (including `ref`) will be forwarded to the default React
 * [`<button>` or `<a>` (reactjs.org)](https://reactjs.org/docs/dom-elements.html).
 */
export const Button = props => {
  const {
      children,
      className,
      compact,
      disabled,
      external,
      externalLinkText,
      finish,
      ghost,
      icon: propIcon,
      iconLabel: propIconLabel,
      iconPos: propIconPos = 'left',
      iconProps,
      loading,
      minified,
      nonInteractive,
      primary,
      size: propSize,
      statusMessage: propStatusMessage = undefined,
      target: propTarget
    } = props,
    buttonProps = _objectWithoutPropertiesLoose(props, _excluded);
  const lc = useLocale();
  const [idOfStatusMessageComponent] = useState(() => "ds-status-message-" + uuidv4());
  let statusMessage;
  if (propStatusMessage) {
    statusMessage = propStatusMessage;
  } else if (propStatusMessage === null || propStatusMessage === false) {
    statusMessage = false;
  } else if (loading) {
    statusMessage = loadingMessages.loading[lc];
  } else {
    statusMessage = undefined;
  }
  const {
      href
    } = buttonProps,
    restButtonProps = _objectWithoutPropertiesLoose(buttonProps, _excluded2);
  const type = getType(props);
  const {
    spacing,
    stacked
  } = useContext(FormContext);
  const {
    registerPrimary,
    stacked: stackedButtonRowContext
  } = useContext(ButtonRowContext);
  const IconComponent = external ? IconLinkExternal : propIcon;
  const iconOnly = !!IconComponent && !children;
  checkIconOnlyProps(iconOnly, type, props);
  const size = propSize || spacing;
  const sizeVariant = size === 'large' || size === 'hero' ? size : undefined;
  let iconSize;
  if (compact) {
    iconSize = 'small';
  } else if (sizeVariant === 'large' || sizeVariant === 'hero') {
    iconSize = 'large';
  } else {
    iconSize = 'default';
  }
  let iconColor;
  if (disabled) {
    iconColor = 'disabled';
  } else if (type === 'alternate' || type === 'finish' || type === 'primary') {
    iconColor = 'default';
  } else {
    iconColor = 'action';
  }
  const iconPos = external ? 'right' : propIconPos;
  let iconLabel;
  if (external) {
    iconLabel = externalLinkText || actionMessages.externalLink[lc];
  } else if (iconOnly) {
    iconLabel = propIconLabel;
  } else {
    iconLabel = undefined;
  }
  const cn = buildVersionedClassNames('ds-button', className, {
    ["ds-button--" + type]: type,
    'ds-button--disabled': disabled,
    'ds-button--ghost': iconOnly && ghost,
    'ds-button--minified': iconOnly && (minified || ghost),
    'ds-button--non-interactive': nonInteractive || loading,
    ["ds-button--icon-" + iconPos]: IconComponent && children,
    ["ds-button--" + sizeVariant]: sizeVariant && !compact,
    'ds-button--wide': stackedButtonRowContext != null ? stackedButtonRowContext : stacked
  });
  const target = external && !propTarget ? '_blank' : propTarget;
  useEffect(() => {
    if (registerPrimary && (primary || finish)) {
      registerPrimary();
    }
  }, []);
  if (isAnchorElement(restButtonProps, disabled, href)) {
    const {
        fwdRef
      } = restButtonProps,
      otherButtonProps = _objectWithoutPropertiesLoose(restButtonProps, _excluded3);
    return /*#__PURE__*/React.createElement("a", _extends({
      "aria-busy": loading,
      "aria-controls": loading && statusMessage ? idOfStatusMessageComponent : undefined,
      className: cn,
      href: href,
      ref: fwdRef,
      target: target
    }, otherButtonProps), contents(disabled, IconComponent, iconSize, iconLabel, iconProps, iconColor, type, loading, children), statusMessage && loading && /*#__PURE__*/React.createElement(StatusMessage, {
      hidden: true,
      id: idOfStatusMessageComponent
    }, statusMessage));
  } else if (isButtonElement(restButtonProps, disabled, href)) {
    const {
        fwdRef,
        onClick
      } = restButtonProps,
      otherButtonProps = _objectWithoutPropertiesLoose(restButtonProps, _excluded4);
    return /*#__PURE__*/React.createElement("button", _extends({
      "aria-busy": loading,
      "aria-controls": loading && statusMessage ? idOfStatusMessageComponent : undefined,
      className: cn,
      disabled: disabled,
      onClick: onClick && !loading && !nonInteractive ? onClick : undefined,
      ref: fwdRef
    }, otherButtonProps), contents(disabled, IconComponent, iconSize, iconLabel, iconProps, iconColor, type, loading, children), statusMessage && loading && /*#__PURE__*/React.createElement(StatusMessage, {
      hidden: true,
      id: idOfStatusMessageComponent
    }, statusMessage));
  } else {
    return null;
  }
};
const isAnchor = props => !!props.href;
const isAnchorElement = (props, disabled, href) => {
  // <a disabled> isn't valid HTML and also has accessibility issues so always render button when disabled
  if (disabled) {
    return false;
  } else {
    return !!href;
  }
};
const isButtonElement = (props, disabled, href) => {
  if (disabled) {
    return true;
  } else {
    return !href;
  }
};

// The if...else is for typing the refs correctly
const Fwd = /*#__PURE__*/forwardRef((props, ref) => {
  if (isAnchor(props)) {
    return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
      fwdRef: ref
    }));
  } else {
    return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
      fwdRef: ref
    }));
  }
});
function contents(disabled, IconComponent, iconSize, iconLabel, iconProps, iconColor, type, loading, children) {
  function getColor() {
    if (type) {
      return type === 'compact' ? colorTokens.icon.button['default'] : colorTokens.icon.button[type];
    }
  }
  const color = getColor();
  return loading ? /*#__PURE__*/React.createElement(Loading, {
    className: buildVersionedClass('ds-button__loading'),
    color: color,
    size: iconSize,
    statusMessage: false
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, IconComponent && /*#__PURE__*/React.createElement(IconComponent, _extends({
    className: buildVersionedClass('ds-button__icon'),
    color: iconColor,
    label: iconLabel,
    onDarkBg: !disabled && (type === 'finish' || type === 'primary'),
    size: iconSize
  }, iconProps)), children);
}
Fwd.displayName = 'Button';
export default Fwd;